import React, { useCallback } from 'react'
import { I18n, CDNImage, hooks } from '@front/volcanion'
import { UserUtils } from '@front/volcanion/utils'
import { IconUtils } from '@front/squirtle/utils'

import { Grid, Box, InputAdornment, Icon } from '@mui/material'
import _ from 'lodash'

import {
  DateTimeField,
  MenuSelectorField,
  ModelAutocompleteField,
  ModelMenuSelectorField,
  SingleOptionSelectorField,
  TextField
} from '@front/squirtle'

import { MenuIcon } from '@front/squirtle/components/MenuSelector/component'

const ChannelIcon = ({ option }) => {
  const icon = IconUtils.getChannelIcon(option?.name, option?.user_type)
  return icon ? <Icon fontSize='small'>{icon}</Icon> : null
}

const ScheduleIcon = ({ option }) => {
  const { icon } = IconUtils.getScheduleTypeIcon(option?.value)
  return icon ? <Icon fontSize='small'>{icon}</Icon> : null
}

const QuickSection = ({ mode }) => {
  const order_status_list = hooks.useOrderStatusList()

  const getStatusColor = useCallback(status => {
    switch (status) {
      case 'created':
      case 'standby':
        return '#E59400'
      case 'attributed':
        return '#0060AC'
      case 'canceled':
        return '#E21818'
      case 'complete':
        return '#4CAF50'
      default:
        return '#0060AC'
    }
  }, [])

  const orderStatusOptions = _.map(order_status_list, elem => ({
    label: I18n.t(`order.status.${elem}`),
    value: elem,
    color: getStatusColor(elem)
  }))

  const getOrderStatusColor = useCallback(option => ({ color: option?.color }), [])

  const autocompleteMutator = useCallback(q => ({
    or: [
      { customerinfo: { client_number: { startsWith: _.split(q, ' ') } } },
      { customerinfo: { name: { startsWith: _.split(q, ' ') } } },
      { info: { first_name: { startsWith: _.split(q, ' ') } } },
      { info: { last_name: { startsWith: _.split(q, ' ') } } }
    ]
  }), [])

  return (
    <Box flex={1} sx={{ maxWidth: '100%' }}>
      <Grid container spacing={1} mb={1}>
        <Grid size={1}>
          <SingleOptionSelectorField name={'priority'} label={I18n.t('priority.label.alter')} />
        </Grid>
        <Grid hidden={mode !== 'order'} size={2}>
          <TextField name={'order_client_id'} label={I18n.t('order.id.alter')} />
        </Grid>
        <Grid size={2}>
          <TextField name={'do.auth.gsm'} label={I18n.t('phone.label', { count: 1 })} />
        </Grid>
        <Grid size={2}>
          <ModelAutocompleteField
            name={'do.user_id'}
            label={I18n.t('user.search_multiple')}
            model_name={'user'}
            searchMode='search'
            searchQuery={'='}
            autocompleteMutator={autocompleteMutator}
            config={{
              forced_filter: { customerinfo: { '!': null } },
              populate: ['info', 'customerinfo'],
              mutator: user => ({
                label: UserUtils.getFormatedName({ ...user?.info, designation: user?.customerinfo?.name }, { prefix: user?.customerinfo?.client_number }),
                ...user
              })
            }}
          />
        </Grid>
        <Grid size={2}>
          <TextField name={'loads.src_contact'} label={I18n.t('transport.passenger')} />
        </Grid>
        <Grid size="grow">
          <MenuSelectorField
            name='status'
            label={I18n.t('order.status.label', { count: 1 })}
            options={orderStatusOptions}
            getOptionProps={getOrderStatusColor}
            multiple
          />
        </Grid>
        <Grid hidden={mode !== 'order'} size="grow">
          <ModelMenuSelectorField
            name='channel'
            label={I18n.t('channel.label', { count: 1 })}
            model_name='domain'
            labelKeys={['name']}
            searchMode={'search'}
            config={{
              initial_filter: {},
              forced_filter: { user_type: { '!': 'driver' } }
            }}
            multiple
          >
            <MenuIcon><ChannelIcon /></MenuIcon>
          </ModelMenuSelectorField>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid hidden={mode !== 'order'} size={1}>
          <MenuSelectorField
            name={'schedule_type'}
            label={I18n.t('order.schedule_type')}
            options={[
              { label: I18n.t('order.planned.label', { count: 1 }), value: "planned" },
              { label: I18n.t('order.immediate.label', { count: 1 }), value: "immediate" }
            ]}
            noneLabel={I18n.t('all')}
          >
            <MenuIcon><ScheduleIcon /></MenuIcon>
          </MenuSelectorField>
        </Grid>
        <Grid size={2.5}>
          <DateTimeField
            name='requestedAt.start'
            dateProps={{ label: I18n.t('date.search'), inputFormat: 'DD/MM/YYYY' }}
            timeProps={{ label: I18n.t('prefix.at'), minTime: null, outputFormat: 'HH[h]mm' }}
            default_time='00:00'
          />
        </Grid>
        <Grid size={2.5}>
          <DateTimeField
            name='requestedAt.end'
            dateProps={{ label: I18n.t('prefix.to'), inputFormat: 'DD/MM/YYYY' }}
            timeProps={{ label: I18n.t('prefix.at'), minTime: null, outputFormat: 'HH[h]mm' }}
            default_time='23:59'
          />
        </Grid>
        <Grid size="grow">
          <TextField name={'source.name'} label={I18n.t('address.source')} InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CDNImage name={'source'} category='map' style={{ width: 18, height: 18 }} />
              </InputAdornment>
            )
          }} />
        </Grid>
        <Grid size={2}>
          <ModelAutocompleteField
            name={'city'}
            label={I18n.t('city.label', { count: 1 })}
            model_name={'city'}
            searchMode={'search'}
            searchQuery={'contains'}
            autocompleteKey={'name'}
            labelKeys={['name_translated']}
            config={{
              initial_filter: {},
              sortBy: ['name_translated'],
              sortDirection: ['asc']
            }}
            multiple
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default React.memo(QuickSection)
